import React, { useState, useEffect } from "react";
import { Paper, Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getParams, searchParams } from "../../../../utlis/helpers";
import { ACTIVITY_MONITORING } from "../../../../routes/routerPath";
import { getCarePlan } from "../../../../services/v5Api";
import { userInfoFetch } from "../../../../utlis/commonFunction";
import {
  formatLastUpdated,
  shouldShowMobility,
  shouldShowActivity,
  shouldShowVitals,
  filterVitalsForDisplay,
  transformCarePlanResponse,
} from "./carePlanUtils";
import "./careplan.scss";
import StatusMetricBlock from "../../../common/StatusMetricBlock";
import ActivityAbnormalities from "../../../common/ActivityAbnormalities";
import NoDataFound from "../../../common/NoDataFound";
import { rewriteReportDomain } from "../../../../configs/environment";

// Import PNG icons
import caregivingIcon from "../../../../assets/icons/caregiving.png";
import therapistIcon from "../../../../assets/icons/therapist.png";
import clinicianIcon from "../../../../assets/icons/clinician.png";
import mobilityIcon from "../../../../assets/icons/mobility.png";
import activityIcon from "../../../../assets/icons/activity.png";
import vitalsIcon from "../../../../assets/icons/vitals.png";

/**
 * Care Plan Component
 *
 * Displays:
 * 1. Header with last updated date
 * 2. Review Care Action Plan - role-based recommendations
 * 3. Input & Insights Used - Mobility, Activity, Vitals sections
 */
const CarePlan = ({ onNavigateTab, onOpenMobilityInsights }) => {
  const navigate = useNavigate();
  const [carePlanData, setCarePlanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const userInfo = userInfoFetch();
  const seniorUuid = userInfo?.id;

  useEffect(() => {
    const loadCarePlanData = async () => {
      if (!seniorUuid) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await getCarePlan(seniorUuid);
        const apiData = response?.data || response;
        const transformed = transformCarePlanResponse(apiData);
        setCarePlanData(transformed);
      } catch (error) {
        console.error("Error loading care plan data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCarePlanData();
  }, [seniorUuid]);

  const handleActivityTrend = () => {
    navigate(ACTIVITY_MONITORING + searchParams);
  };

  const buildRomReportUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;

    const { accessToken, providerId, adminId, clientId } = getParams();
    const withV2 = String(rawUrl).replace(
      /side-tug-reportv1/g,
      "side-tug-reportv2",
    );
    // Force the configured REPORTS_URL domain regardless of what the backend returned.
    const domainRewritten = rewriteReportDomain(withV2);

    try {
      const url = new URL(domainRewritten);
      const paramsToAdd = {
        isWebsite: "true",
        accessToken,
        providerId,
        adminId,
        clientId,
      };
      Object.entries(paramsToAdd).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }
      });
      return url.toString();
    } catch {
      // Fallback: if URL parsing fails, return the normalized URL.
      return domainRewritten;
    }
  };

  const handleWellnessReport = (url) => {
    if (url) {
      window.location.href = buildRomReportUrl(url);
    } else {
      // TODO: Navigate to wellness report
      console.log("Navigate to Wellness Report & Exercise Details");
    }
  };

  const getRoleIcon = (iconType) => {
    switch (iconType) {
      case "caregiving":
        return (
          <img
            src={caregivingIcon}
            alt="Caregiving"
            className="role-icon-img"
          />
        );
      case "therapist":
        return (
          <img src={therapistIcon} alt="Therapist" className="role-icon-img" />
        );
      case "clinician":
        return (
          <img src={clinicianIcon} alt="Clinician" className="role-icon-img" />
        );
      default:
        return (
          <img
            src={caregivingIcon}
            alt="Caregiving"
            className="role-icon-img"
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div
        className="care-plan-container loading"
        style={{
          margin: "auto",
          marginTop: "2rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </div>
    );
  }

  if (!carePlanData) {
    return (
      <div className="care-plan-container">
        <NoDataFound
          icon="care"
          message="No care plan available"
          description="The care plan will appear here once it has been generated for this person."
        />
      </div>
    );
  }

  const { lastUpdated, recommendations, insights } = carePlanData;
  const { mobility, activity, vitals } = insights || {};
  const showMobility = shouldShowMobility(mobility);
  const showActivity = shouldShowActivity(activity);
  const showVitals = shouldShowVitals(vitals);
  const vitalsToMonitor = filterVitalsForDisplay(vitals?.metrics || []);

  return (
    <div className="care-plan-container">
      {/* Last Updated */}
      <div className="last-updated">
        Last Updated: {formatLastUpdated(lastUpdated)}
      </div>

      {/* Review Care Action Plan Section */}
      <section className="care-plan-section recommendations-section">
        <Typography className="section-main-title">
          Review Care Action Plan
        </Typography>

        {recommendations?.map((rec) => (
          <div key={rec.role} className="recommendation-card">
            <div className="recommendation-header">
              <div className="recommendation-icon">{getRoleIcon(rec.icon)}</div>
              <Typography className="recommendation-label">
                {rec.label}
              </Typography>
            </div>
            <Paper elevation={0} className="recommendation-content">
              <Typography className="recommendation-text">
                {rec.text}
              </Typography>
            </Paper>
          </div>
        ))}
      </section>

      {/* Input & Insights Used Section */}
      <section className="care-plan-section insights-section">
        <Typography className="section-main-title">
          Input & Insights Used
        </Typography>

        {/* Mobility Subsection */}
        {showMobility && (
          <div className="insight-subsection">
            <div className="subsection-header">
              <div className="subsection-icon">
                <img
                  src={mobilityIcon}
                  alt="Mobility"
                  className="subsection-icon-img"
                />
              </div>
              <Typography className="subsection-title">Mobility</Typography>
            </div>

            <Paper elevation={0} className="insight-card">
              {/* Risk Tier */}
              {/* <Typography className="card-label">Mobility Risk Tier</Typography> */}
              <div className="risk-tier-row">
                <div style={{ flex: 1 }}>
                  {" "}
                  {/* Wrap block to control width if needed or let it flex */}
                  <StatusMetricBlock
                    label="Mobility Risk Tier"
                    value={mobility.riskTier.label}
                    status={mobility.riskTier.status || mobility.riskTier.level}
                  />
                </div>
              </div>

              {/* Latest Risk Analysis Link */}
              {mobility.reportId ? (
                <div className="risk-analysis-link-container">
                  <button
                    type="button"
                    className="risk-analysis-link"
                    onClick={() => onOpenMobilityInsights?.(mobility.reportId)}
                  >
                    Latest Risk Analysis
                  </button>
                </div>
              ) : (
                <div className="no-data-prompt">
                  <Typography className="no-data-text">
                    No risk analysis available yet. Conduct a mobility
                    assessment to generate insights.
                  </Typography>
                  <button
                    className="btn btn-outline"
                    onClick={() => onNavigateTab?.("risk")}
                  >
                    Start Mobility Assessment
                  </button>
                </div>
              )}

              {/* Divider */}
              <div className="section-divider">
                <span className="divider-line"></span>
                <span className="divider-text">
                  Mobility Metrics Performance
                </span>
                <span className="divider-line"></span>
              </div>

              {/* Metrics Performance */}
              <div className="metrics-grid">
                {mobility.metricsPerformance.map((metric) => (
                  <StatusMetricBlock
                    key={metric.key}
                    label={metric.label}
                    value={metric.statusLabel}
                    status={metric.status}
                  />
                ))}
              </div>

              {/* Wellness Report Button */}
              {mobility.wellnessReportUrl ? (
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    handleWellnessReport(mobility.wellnessReportUrl)
                  }
                >
                  Wellness Report & Exercise
                </button>
              ) : (
                <div className="no-data-prompt">
                  <Typography className="no-data-text">
                    No wellness report generated yet. Complete a mobility test
                    to receive a personalized wellness report and exercise plan.
                  </Typography>
                  <button
                    className="btn btn-outline"
                    onClick={() => onNavigateTab?.("metrics")}
                  >
                    Go to Metrics
                  </button>
                </div>
              )}
            </Paper>
          </div>
        )}

        {/* Activity Subsection */}
        {showActivity && (
          <div className="insight-subsection">
            <div className="subsection-header">
              <div className="subsection-icon">
                <img
                  src={activityIcon}
                  alt="Activity"
                  className="subsection-icon-img"
                />
              </div>
              <Typography className="subsection-title">Activity</Typography>
            </div>

            <Paper elevation={0} className="insight-card">
              <ActivityAbnormalities
                fallAlerts={activity.fallAlerts}
                abnormalities={activity.abnormalities}
              />

              {/* Activity Trend Button */}
              <button className="btn btn-primary" onClick={handleActivityTrend}>
                Activity Trend
              </button>
            </Paper>
          </div>
        )}

        {/* Vitals Subsection */}
        {showVitals && (
          <div className="insight-subsection">
            <div className="subsection-header">
              <div className="subsection-icon">
                <img
                  src={vitalsIcon}
                  alt="Vitals"
                  className="subsection-icon-img"
                />
              </div>
              <Typography className="subsection-title">Vitals</Typography>
            </div>

            <Paper elevation={0} className="insight-card">
              {/* Vitals Grid */}
              <div className="metrics-grid">
                {vitalsToMonitor.map((vital) => (
                  <StatusMetricBlock
                    key={vital.key}
                    label={vital.label}
                    value={vital.displayValue}
                    status={vital.status}
                  />
                ))}
              </div>

              {/* Vitals Trend Button */}
              {/* Button Removed per request */}
              {!vitals?.metrics?.some((v) => v.status !== "no_data") && (
                <div className="no-data-prompt">
                  <Typography className="no-data-text">
                    No vitals data recorded yet. Collect vitals readings to
                    track trends and monitor health over time.
                  </Typography>
                  <button
                    className="btn btn-outline"
                    onClick={() => onNavigateTab?.("monitor")}
                  >
                    Go to Monitor
                  </button>
                </div>
              )}
            </Paper>
          </div>
        )}
      </section>
    </div>
  );
};

export default CarePlan;
